import { useEffect, useMemo, useState } from 'react';

import { formatChordLabel } from 'app/modules/wallforming/components/input-binding/utils/inputChordUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { InputActionId } from 'app/modules/wallforming/components/input-binding/types/inputActionId';
import type { KeyboardLayoutMap } from 'app/modules/wallforming/components/input-binding/types/keyboardLayoutMap';

type NavigatorWithKeyboard = Navigator & { keyboard?: { getLayoutMap?: () => Promise<KeyboardLayoutMap> } };

let layoutMap: KeyboardLayoutMap | undefined;
let layoutRequest: Promise<KeyboardLayoutMap | undefined> | undefined;

/** Requested once per session; `navigator.keyboard` is not available in every browser. */
const loadKeyboardLayout = () =>
    (layoutRequest ??= ((navigator as NavigatorWithKeyboard).keyboard?.getLayoutMap?.() ?? Promise.resolve(undefined))
        .then((map) => (layoutMap = map))
        .catch(() => undefined));

export function useKeyboardLayout(): KeyboardLayoutMap | undefined {
    const [map, setMap] = useState(layoutMap);
    useEffect(() => {
        if (!map) {
            void loadKeyboardLayout().then(setMap);
        }
    }, [map]);
    return map;
}

/** Live label of the bindings of an action in the active profile, e.g. for menu shortcut hints. */
export function useGetInputActionLabel(actionId: InputActionId): string {
    const slots = usePlacementStore((state) => state.inputBindings.slots[actionId]);
    const keyboardLayout = useKeyboardLayout();
    return useMemo(
        () => slots.flatMap((chord) => (chord ? [formatChordLabel(chord, keyboardLayout)] : [])).join(' / '),
        [keyboardLayout, slots]
    );
}
